export const dispatchInputEvent = (
  newValue: string,
  element: HTMLInputElement | HTMLTextAreaElement
) => {
  // Prefer prototype setter to keep React's value tracker in sync
  const protoSetter =
    element instanceof HTMLInputElement
      ? Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')
          ?.set
      : Object.getOwnPropertyDescriptor(HTMLTextAreaElement.prototype, 'value')
          ?.set;

  if (protoSetter) {
    protoSetter.call(element, newValue);
  } else {
    // Safe fallback; both input and textarea expose .value
    element.value = newValue;
  }

  element.dispatchEvent(
    new Event('input', { bubbles: true, cancelable: true })
  );
};

export const dispatchChangeEvent = <T extends HTMLElement>(
  newValue: string,
  element: T
) => {
  element.dispatchEvent(new Event('change', { bubbles: true }));
};

/**
 * HTML 콘텐츠에서 첫 번째 이미지의 src URL을 추출합니다.
 * @param htmlContent HTML 콘텐츠 문자열
 * @returns 첫 번째 이미지의 src URL 또는 null
 */
export const extractFirstImageUrl = (htmlContent: string): string | null => {
  if (!htmlContent || typeof htmlContent !== 'string') {
    return null;
  }

  // HTML 문자열에서 img 태그의 src 속성을 찾는 정규식
  const imgRegex = /<img[^>]+src\s*=\s*["']([^"']+)["'][^>]*>/i;
  const match = htmlContent.match(imgRegex);

  return match ? match[1] : null;
};

const SCROLL_LOCK_COUNT_DATASET_KEY = 'scrollLockCount';
const SCROLL_LOCK_OVERFLOW_DATASET_KEY = 'scrollLockPrevOverflow';
const SCROLL_LOCK_PADDING_DATASET_KEY = 'scrollLockPrevPaddingRight';

export const lockBodyScroll = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return () => {};
  }

  const { body, documentElement } = document;
  const currentCount = Number(body.dataset[SCROLL_LOCK_COUNT_DATASET_KEY] ?? 0);

  if (currentCount === 0) {
    body.dataset[SCROLL_LOCK_OVERFLOW_DATASET_KEY] = body.style.overflow;
    body.dataset[SCROLL_LOCK_PADDING_DATASET_KEY] = body.style.paddingRight;

    const scrollbarWidth = window.innerWidth - documentElement.clientWidth;
    const computedPaddingRight =
      Number.parseFloat(window.getComputedStyle(body).paddingRight) || 0;

    body.style.overflow = 'hidden';

    if (scrollbarWidth > 0) {
      body.style.paddingRight = `${computedPaddingRight + scrollbarWidth}px`;
    }
  }

  body.dataset[SCROLL_LOCK_COUNT_DATASET_KEY] = String(currentCount + 1);

  return () => {
    const nextCount =
      Number(body.dataset[SCROLL_LOCK_COUNT_DATASET_KEY] ?? 1) - 1;

    if (nextCount > 0) {
      body.dataset[SCROLL_LOCK_COUNT_DATASET_KEY] = String(nextCount);
      return;
    }

    body.style.overflow = body.dataset[SCROLL_LOCK_OVERFLOW_DATASET_KEY] ?? '';
    body.style.paddingRight =
      body.dataset[SCROLL_LOCK_PADDING_DATASET_KEY] ?? '';

    delete body.dataset[SCROLL_LOCK_COUNT_DATASET_KEY];
    delete body.dataset[SCROLL_LOCK_OVERFLOW_DATASET_KEY];
    delete body.dataset[SCROLL_LOCK_PADDING_DATASET_KEY];
  };
};
